import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { dialogTabStops } from './dialog-tab-stops';
import './dialog-header.css';

/**
 * `telemetry`: the window's code name for product telemetry (ADR-0081), a literal at every use (tests/telemetry-tests
 * checks it); it names the window, never its title, which can carry a job or type name.
 * `complete`: the window's work is done (a sign-up saved), so closing it now reports a completed dialog, not one
 * the person left (telemetry.ts, as TourStart.tsx's reading window).
 */
export function WorkspaceDialog({
  telemetry,
  title,
  description,
  onClose,
  children,
  actions,
  compact = false,
  suspended = false,
  complete = false,
}: {
  telemetry: string;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  compact?: boolean;
  suspended?: boolean;
  complete?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const id = useId();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      const dialogs = [...document.querySelectorAll('[role="dialog"]')];
      if (dialogs.filter((dialog) => dialog.getClientRects().length > 0).at(-1) !== ref.current)
        return;
      if (event.defaultPrevented || ref.current?.querySelector('[role="menu"]')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        close.current();
      }
      if (event.key !== 'Tab' || !ref.current) return;
      // Match actual Tab stops, including multiline editors and disclosures.
      // :disabled also covers controls inside a disabled fieldset; geometry
      // alone does not exclude visibility:hidden or explicit tabindex=-1.
      const items = dialogTabStops(ref.current);
      const first = items[0],
        last = items.at(-1);
      if (!first) {
        event.preventDefault();
        ref.current.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === ref.current)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !ref.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return createPortal(
    <div
      className="workspace-dialog-backdrop"
      style={suspended ? { display: 'none' } : undefined}
      onMouseDown={(event) => {
        if (event.button === 0 && event.target === event.currentTarget) {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <section
        ref={ref}
        tabIndex={-1}
        role="dialog"
        data-telemetry-dialog={telemetry}
        data-telemetry-complete={complete ? '' : undefined}
        aria-modal="true"
        aria-labelledby={id}
        aria-describedby={description ? `${id}-description` : undefined}
        className={`workspace-dialog${compact ? ' compact' : ''}`}
      >
        <header className="workspace-dialog-heading">
          <div className="workspace-dialog-title">
            <h2 id={id}>{title}</h2>
            {description && <p id={`${id}-description`}>{description}</p>}
          </div>
          {actions && <div className="workspace-dialog-actions">{actions}</div>}
          <button className="icon-button" aria-label={`Close ${title}`} onClick={onClose}>
            <X size={20} />
          </button>
        </header>
        <div className="workspace-dialog-body">{children}</div>
      </section>
    </div>,
    document.body,
  );
}
